import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ImageCentering() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎯 Advanced CSS: Image Centering</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Centering images is a common CSS task. You can align images
        horizontally, vertically, or both using text alignment, margin auto,
        flexbox, or CSS grid.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>text-align: center;</code> for inline images.
          </li>
          <li>
            Use <code>margin: auto;</code> with <code>display: block;</code> to
            center block images.
          </li>
          <li>
            Flexbox makes horizontal and vertical centering easy with{" "}
            <code>justify-content</code> and <code>align-items</code>.
          </li>
          <li>
            CSS Grid allows perfect centering using{" "}
            <code>place-items: center;</code>.
          </li>
          <li>
            Always ensure the parent container has a defined height for vertical
            centering.
          </li>
        </ul>
      </div>

      {/* Example 1: Text-align */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Center with text-align</h2>
        <CodeCopyBlock
          code={`div {
  text-align: center;
}`}
        />
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <img
            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80"
            alt="Text-align center"
            style={{ width: "250px", borderRadius: "12px" }}
          />
        </div>
      </div>

      {/* Example 2: Margin Auto */}
      <div className="tutorial-section">
        <h2 className="section-title">🖌️ Center with margin auto</h2>
        <CodeCopyBlock
          code={`img {
  display: block;
  margin: 0 auto;
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=300&q=80"
          alt="Margin auto center"
          style={{
            display: "block",
            margin: "1rem auto",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Example 3: Flexbox */}
      <div className="tutorial-section">
        <h2 className="section-title">📐 Center with Flexbox</h2>
        <CodeCopyBlock
          code={`div {
  display: flex;
  justify-content: center; /* horizontal */
  align-items: center;    /* vertical */
  height: 200px;
}`}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            backgroundColor: "#e0f2fe",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <img
            src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=300&q=80"
            alt="Flexbox center"
            style={{ width: "150px", borderRadius: "12px" }}
          />
        </div>
      </div>

      {/* Example 4: Grid */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Center with Grid</h2>
        <CodeCopyBlock
          code={`div {
  display: grid;
  place-items: center; /* horizontal + vertical */
  height: 200px;
}`}
        />
        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: "200px",
            backgroundColor: "#fde68a",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <img
            src="https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=300&q=80"
            alt="Grid center"
            style={{ width: "150px", borderRadius: "12px" }}
          />
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Combine centering methods with responsive design techniques.</li>
          <li>Use Flexbox or Grid for complex layouts with multiple images.</li>
          <li>Apply CSS transitions and animations on centered images.</li>
          <li>
            Experiment with overlapping elements while keeping images centered.
          </li>
        </ul>
      </div>
    </div>
  );
}
